import dayjs from "dayjs";

// ─────────────────────────────────────────────────────────────────────────────
// Shared validation utilities for Plan of Care and Progress Notes
// Extracted from PlanOfCarePanel, PlanOfCareForm, PlanOfCareEndDateDialog
// ─────────────────────────────────────────────────────────────────────────────

export const DECISION_TYPES = [
  { id: "Discharge", name: "Discharge Client" },
  { id: "Transition", name: "Transition to New Level of Care" },
  { id: "Continue", name: "Continue Same Level of Care" },
];

/**
 * Validate Plan of Care header fields.
 * @param {{ levelOfCareId, startDate, estimatedDischargeDate }} values
 * @returns {{ [field: string]: string }} errors — empty object means valid
 */
export const validatePlanHeader = (values) => {
  const errors = {};
  if (!values.levelOfCareId && values.levelOfCareId !== 0)
    errors.levelOfCareId = "Level of Care is required.";
  if (!values.startDate)
    errors.startDate = "Start Date is required.";
  if (!values.estimatedDischargeDate)
    errors.estimatedDischargeDate = "Estimated Discharge Date is required.";
  if (
    values.startDate &&
    values.estimatedDischargeDate &&
    dayjs(values.estimatedDischargeDate).isBefore(dayjs(values.startDate), "day")
  ) {
    errors.estimatedDischargeDate =
      "Estimated Discharge Date must be on or after Start Date.";
  }
  return errors;
};

/**
 * Validate end-date decision fields.
 * @param {{ decisionType, newLevelOfCareId, newEstimatedDischargeDate }} values
 * @param {string|null} currentEndDate  ISO string — minimum for new discharge date
 * @returns {{ [field: string]: string }} errors
 */
export const validateDecision = (values, currentEndDate = null) => {
  const errors = {};
  if (!values.decisionType)
    errors.decisionType = "Please select a decision.";
  if (values.decisionType === "Discharge" && !values.lastDateOfService)
    errors.lastDateOfService = "Last Date of Service is required.";
  if (values.decisionType === "Transition") {
    if (!values.newLevelOfCareId)
      errors.newLevelOfCareId = "New Level of Care is required.";
    if (!values.newEstimatedDischargeDate)
      errors.newEstimatedDischargeDate = "New Estimated Discharge Date is required.";
  }
  if (values.decisionType === "Continue") {
    if (!values.newEstimatedDischargeDate)
      errors.newEstimatedDischargeDate = "New Estimated Discharge Date is required.";
  }
  if (
    currentEndDate &&
    values.newEstimatedDischargeDate &&
    dayjs(values.newEstimatedDischargeDate).isBefore(dayjs(currentEndDate), "day")
  ) {
    errors.newEstimatedDischargeDate =
      "New Estimated Discharge Date must be on or after the current end date.";
  }
  return errors;
};

/**
 * Resolve provider user display name from a user list.
 * @param {Array} users
 * @param {string|number|null} userId
 * @returns {string}
 */
export const resolveProviderName = (users, userId) => {
  if (!userId || !Array.isArray(users)) return "—";
  const id = String(userId);
  const user = users.find((u) => String(u.id ?? u.userId ?? "") === id);
  if (!user) return id;
  return user.fullName ?? (`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email || id);
};

/**
 * Build a userId → display name map from a user list.
 * @param {Array} users
 * @returns {{ [id: string]: string }}
 */
export const buildUserMap = (users) => {
  const map = {};
  if (!Array.isArray(users)) return map;
  users.forEach((u) => {
    const id = String(u.id ?? u.userId ?? "");
    if (id) map[id] = u.fullName ?? (`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email) ?? id;
  });
  return map;
};

/**
 * Build a roleId → role name map from a role list.
 * @param {Array} roles
 * @returns {{ [id: string]: string }}
 */
export const buildRoleMap = (roles) => {
  const map = {};
  if (!Array.isArray(roles)) return map;
  roles.forEach((r) => {
    if (r.id != null) map[String(r.id)] = r.name;
  });
  return map;
};

/**
 * Build a serviceId → service name map from a service catalog.
 * @param {Array} services
 * @returns {{ [id: string]: string }}
 */
export const buildServiceMap = (services) => {
  const map = {};
  if (!Array.isArray(services)) return map;
  services.forEach((s) => {
    const key = s.id ?? s.serviceId ?? s.value;
    if (key != null) map[String(key)] = s.name ?? s.label ?? String(key);
  });
  return map;
};

/**
 * Format a date value to MM/DD/YYYY, returning "—" if falsy.
 * @param {string|Date|null} val
 * @returns {string}
 */
export const formatDate = (val) =>
  val ? dayjs(val).format("MM/DD/YYYY") : "—";

/**
 * Format a TimeSpan string "HH:mm:ss" to "h:mm AM/PM".
 * @param {string|null} ts
 * @returns {string|null}
 */
export const formatTimeStr = (ts) => {
  if (!ts) return null;
  const [h, m] = ts.substring(0, 5).split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};

/**
 * Calculate duration in minutes between two TimeSpan strings.
 * @param {string|null} startTs "HH:mm" or "HH:mm:ss"
 * @param {string|null} endTs
 * @returns {number|null}
 */
export const calcDurationMins = (startTs, endTs) => {
  if (!startTs || !endTs) return null;
  const [sh, sm] = startTs.substring(0, 5).split(":").map(Number);
  const [eh, em] = endTs.substring(0, 5).split(":").map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : null;
};

/** Service dot-color mapping (shared across table and plan views). */
export const SERVICE_COLORS = {
  "Intake Assessment": "#FFA07A",
  "History and Physical": "#20B2AA",
  "Medical Progress Note": "#9370DB",
  "Nursing Detox": "#FF4500",
  "Partial Hospitalization Group therapy": "#4682B4",
  "Intensive Outpatient Group Therapy": "#32CD32",
  "Traditional Outpatient group therapy": "#FFD700",
  "3.1 Residential per diem": "#8B0000",
  "3.1 Room and Board": "#008080",
  "Individual Therapy": "#FF69B4",
  "Crisis Intervention": "#00CED1",
  "Case Management - Recovery Support": "#6A5ACD",
  "Urine screen": "#D2691E",
  "Family Therapy with Client": "#40E0D0",
  "Family Therapy without Client": "#DC143C",
  "Clinical update": "#7CFC00",
  "Vivitrol Injection": "#DAA520",
  "Substance Abuse Group Session": "#6495ED",
  "Substance Abuse Individual Session": "#BA55D3",
  "Discharge": "#2E8B57",
};
